import TagManager from 'react-gtm-module';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './search-bar.module.scss';
import Typeahead, { SuggestionResult } from '../../atoms/typeahead/typeahead';
import { ApplicationSettings } from '../../../models/settings';
import { GetSearchSuggestions } from '../../../api-clients/search-api-client';
import { CrossIcon, SearchIcon } from '../../atoms/icons/icons';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { useSearchParams } from 'next/navigation';
import { useQueryKeyword } from '../../../hooks/use-query-keyword.hook';
import { useTranslations } from 'next-intl';

type Props = {
  idPrefix: string;
  acceptEmptyValue?: boolean;
  customClassName?: string;
  cssWrapper?: string;
  typeAheadPlaceHolder: string;
  applicationSettings: ApplicationSettings;
  typeaheadActive?: boolean;
  currentLocale: string;
  themeColor?: string;
  themeCopyColor?: string;
  invertedTheme?: boolean;
  brandPrimaryColor?: string;
  getSearchSuggestions?: GetSearchSuggestions;
};

function pushDataLayer(keywordQuery: string) {
  if (process.env.NEXT_PUBLIC_GTM_CONTAINER_ID) {
    TagManager.dataLayer({
      dataLayer: { searchTerms: keywordQuery, event: `searchTerms` },
    });
  }
}

export default function SearchBar({
  idPrefix,
  acceptEmptyValue = false,
  customClassName = null,
  cssWrapper,
  typeAheadPlaceHolder,
  applicationSettings,
  typeaheadActive = true,
  currentLocale,
  themeColor,
  themeCopyColor,
  invertedTheme,
  brandPrimaryColor,
  getSearchSuggestions,
}: Props) {
  const searchParams = useSearchParams();

  const queryKeyword = useQueryKeyword(searchParams);

  const initialKeyword = queryKeyword.getKeyword();

  const [keyword, setKeywordValue] = useState(initialKeyword);
  const [lastKeyword, setLastKeyword] = useState(initialKeyword);
  const [resetTypeahead, setResetTypeahead] = useState<boolean>(false);

  const translate = useTranslations(`Common`);

  useEffect(() => {
    setKeywordValue(initialKeyword);
  }, [initialKeyword]);

  const onTypeaheadChangeHandler = (searchTerm: string) => {
    setKeywordValue(searchTerm);
  };

  const onSuggestionEnter = (keyword: string, suggestionResult: SuggestionResult) => {
    if (suggestionResult?.searchUrl) {
      window.location.href = `/${currentLocale}${suggestionResult.searchUrl}`;

      return;
    }

    handleSearchButton(keyword);
  };

  function handleSearchButton(keywordEntered: string) {
    if (keywordEntered.length && keywordEntered !== lastKeyword) {
      // push to XO Tracking via GTM dataLayer
      pushDataLayer(keywordEntered);
      setLastKeyword(keywordEntered);
    }
    if (!keywordEntered.length && acceptEmptyValue) {
      setLastKeyword(keywordEntered);
    }

    const path = keywordEntered.length > 0 ? `/search?keyword=${keywordEntered}` : `/search`;

    const searchPath = `/search`;

    const isSearchPage = (): boolean => window.location.href.includes(searchPath);

    if (isSearchPage && keywordEntered.length > 0) {
      queryKeyword.setKeyword(keywordEntered);
    } else {
      window.location.href = path;
    }
  }

  function handleSearchButtonClick() {
    handleSearchButton(keyword);
  }

  const searchApiSettings = {
    baseUrl: `${process.env.NEXT_PUBLIC_SEARCH_SERVICE_URL}/v2`,
    searchTimeOut: 10000,
    applicationType: applicationSettings?.applicationType,
    b2cFlag: applicationSettings?.b2cFlag,
  };

  const searchButtonStyles: React.CSSProperties = useMemo(() => {
    return {
      '--theme-color': themeColor,
      '--brand-primary-color': brandPrimaryColor,
    } as React.CSSProperties;
  }, [themeColor, brandPrimaryColor]);

  const handleResetClick = () => {
    // Set the flag to trigger the reset in the child
    setResetTypeahead(true);
    // Reset the flag shortly after to avoid repeated resets
    setTimeout(() => {
      setResetTypeahead(false);
    }, 0);
  };

  return (
    <div
      className={`${styles[cssWrapper]} ${invertedTheme ? styles['inverted-theme'] : ''}`}
      style={searchButtonStyles}
    >
      <form
        className={`${styles.search} ${styles[customClassName]} ${styles[`${idPrefix}-form`]} search`}
        id={`${idPrefix}-bar`}
        role='search'
      >
        <Typeahead
          typeaheadInputId={idPrefix}
          typeaheadInputPlaceholder={typeAheadPlaceHolder}
          typeaheadInputInitialValue={keyword}
          typeaheadInputValueChange={onTypeaheadChangeHandler}
          cssWrapper={cssWrapper}
          minimumTextLength={3}
          searchApiSettings={searchApiSettings}
          currentLocale={currentLocale}
          themeCopyColor={themeCopyColor}
          getSearchSuggestions={getSearchSuggestions}
          resetTypeahead={resetTypeahead}
          invertedTheme={invertedTheme}
          onSuggestionEnter={onSuggestionEnter}
          typeaheadActive={typeaheadActive}
        />
        <div className={styles[`buttons-wrapper`]}>
          {keyword.length > 0 && cssWrapper === `header` && (
            <button
              type='button'
              className={`${styles[`cross-button`]} image-wrapper`}
              onClick={typeaheadActive ? handleResetClick : () => setKeywordValue(``)}
              id={`${idPrefix}-cross-button`}
            >
              <CrossIcon fillColor={colorStyle.grey3} height={24} width={24} />
            </button>
          )}
          {(cssWrapper === `header` || cssWrapper === 'microsite-ordered-search') && (
            <button
              type='button'
              className={`${styles[`search-button`]}`}
              id={`${idPrefix}-button`}
              aria-label={`${idPrefix}-input`}
              onClick={handleSearchButtonClick}
            >
              <SearchIcon fillColor={colorStyle.white} height={16} width={16} />
            </button>
          )}
        </div>
        {cssWrapper === `homepage` && (
          <button
            className={`${styles[`hero-search-btn`]}`}
            type='button'
            id='heroAttraqtSearchButton'
            aria-label={translate(`homepage.find-programs`)}
            onClick={handleSearchButtonClick}
          >
            {translate(`homepage.find-programs`)}
          </button>
        )}
      </form>
    </div>
  );
}
